import { useCallback, useRef, useState } from 'react';

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'unsupported' | 'denied';

/** Pick the best supported audio mime for MediaRecorder. */
function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch { /* ignore */ }
  }
  return null;
}

/**
 * Minimal voice recorder around MediaRecorder. Hard-stops at maxSeconds.
 * On stop, resolves the recorded Blob + duration via the onDone callback.
 */
export function useVoiceRecorder(opts: {
  maxSeconds: number;
  onDone: (blob: Blob, durationMs: number) => void;
}) {
  const [state, setState] = useState<RecorderState>(
    typeof MediaRecorder === 'undefined' || !pickMime() ? 'unsupported' : 'idle',
  );
  const [elapsedMs, setElapsedMs] = useState(0);

  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const cleanup = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    tickRef.current = null;
    stopTimerRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    const mime = pickMime();
    if (!mime) { setState('unsupported'); return; }
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recRef.current = rec;
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const dur = Date.now() - startedRef.current;
        const blob = new Blob(chunksRef.current, { type: mime });
        cleanup();
        setState('idle');
        setElapsedMs(0);
        if (!cancelledRef.current && blob.size > 0) opts.onDone(blob, dur);
      };
      rec.start();
      startedRef.current = Date.now();
      setState('recording');
      setElapsedMs(0);
      tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedRef.current), 200);
      stopTimerRef.current = setTimeout(() => {
        if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
      }, opts.maxSeconds * 1000);
    } catch {
      cleanup();
      setState('denied');
    }
  }, [cleanup, opts]);

  const stop = useCallback(() => {
    cancelledRef.current = false;
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    else { cleanup(); setState('idle'); setElapsedMs(0); }
  }, [cleanup]);

  return { state, elapsedMs, start, stop, cancel };
}
